import { useId, type ReactNode } from 'react';

export interface FieldProps {
  /** Already translated by the caller, which knows its own namespace. */
  readonly label: string;
  readonly hint?: string;
  /** Translated message; its presence is what marks the field invalid. */
  readonly error?: string | null;
  readonly required?: boolean;
  /** Receives the id and the aria wiring — spread it onto the control. */
  readonly children: (props: {
    id: string;
    'aria-describedby': string | undefined;
    'aria-invalid': boolean | undefined;
    required: boolean | undefined;
  }) => ReactNode;
}

/**
 * One labelled control, with its hint and its error, ported as the field shape
 * of TMS `QuoteForm`.
 *
 * The control is a render prop rather than a wrapped `<input>` so a field can
 * hold a select, a date picker or a whole grid without this component growing a
 * `type` prop per case — which is how the TMS form ended up with a different
 * layout for each field.
 *
 * The wiring is the point: an error is announced through `aria-describedby` and
 * `aria-invalid`, so a screen reader reaches it, rather than being red text
 * that happens to sit nearby.
 */
export function Field({ label, hint, error, required, children }: FieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;

  const describedBy = [hint ? hintId : null, error ? errorId : null]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="flex flex-col">
      <label htmlFor={id} className="mb-1 block text-sm text-muted">
        {label}
        {required && (
          <span aria-hidden="true" className="ml-0.5 text-danger">
            *
          </span>
        )}
      </label>

      {children({
        id,
        'aria-describedby': describedBy === '' ? undefined : describedBy,
        'aria-invalid': error ? true : undefined,
        required: required ? true : undefined,
      })}

      {hint && (
        <p id={hintId} className="mt-1 text-xs text-muted">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} role="alert" className="mt-1 text-sm text-danger">
          {error}
        </p>
      )}
    </div>
  );
}

/** The class every plain text control in a form shares. */
export const FIELD_CONTROL_CLASS =
  'nt-control nt-control-select w-full text-sm placeholder:text-muted';
